import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import AppTooltip from "../../../../components/shared/AppTooltip/AppTooltip";
import { Card } from "../shared/AnalyticsWidgets";
import RoutePieChart, { routeName } from "../charts/RoutePieChart";
import { formatNumber, hasNumber, validRows } from "../../utils/analyticsFormat";
import common from "../../OrderAnalyticsPage.module.scss";
import styles from "./RoutesByMode.module.scss";

const pageSize = 12;
const currencyOrder = ["KZT", "USD", "EUR"];
const currencySymbols = { KZT: "₸", USD: "$", EUR: "€" };

const normalizeSearchKey = (value) =>
  typeof value === "string"
    ? value.normalize("NFKC").toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, "")
    : "";

function sortRoutes(rows, sort, language) {
  return [...rows].sort((a, b) => {
    if (sort.key === "route") return routeName(a).localeCompare(routeName(b), language) * sort.direction;
    const left = a[sort.key];
    const right = b[sort.key];
    // Missing figures stay last in both directions; zero is a real value.
    if (!hasNumber(left)) return hasNumber(right) ? 1 : 0;
    if (!hasNumber(right)) return -1;
    return (left - right) * sort.direction;
  });
}

export default function RoutesByMode({
  routesByMode,
  topRoutes,
  mode = "all",
  onAskAssistant,
  assistantPending,
}) {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState({ key: "orders", direction: -1 });
  const [expanded, setExpanded] = useState({});
  const [showChartsDuringSearch, setShowChartsDuringSearch] = useState(false);
  const tooltipId = useId();
  const number = (value, digits = 0) => formatNumber(value, i18n.language, digits);
  const modeLabels = { auto: t("analyticsNew.mode.auto", "Авто"), air: t("analyticsNew.mode.air", "Авиа") };
  const currencyNames = {
    KZT: t("analyticsNew.directions.tenge", "Тенге"),
    USD: t("analyticsNew.directions.dollars", "Доллары"),
    EUR: t("analyticsNew.directions.euros", "Евро"),
  };
  const columns = [
    { key: "route", label: t("analyticsNew.directions.route", "Маршрут") },
    { key: "orders", label: t("analyticsNew.directions.orders", "Заказов") },
    {
      key: "sharePercent",
      label: t("analyticsNew.directions.share", "Доля"),
      hint: t(
        "analyticsNew.directions.shareHint",
        "Доля заказов маршрута от общего количества заказов в рамках одной валюты.",
      ),
    },
    {
      key: "avgPrice",
      label: t("analyticsNew.directions.avgPrice", "Ср. ставка"),
      money: true,
      hint: t(
        "analyticsNew.directions.avgPriceHint",
        'Средняя ставка по тендерам рассчитывается со статуса "Подтвержден" и далее. Если стоит прочерк (—), заказ находится в статусах «Ожидание предложения» и "Требуется действие".',
      ),
    },
    {
      key: "turnover",
      label: t("analyticsNew.directions.turnover", "Оборот"),
      money: true,
      hint: t(
        "analyticsNew.directions.turnoverHint",
        "Сумма принятых ставок по всем заказам маршрута.",
      ),
    },
    {
      key: "firstBidMinutes",
      label: t("analyticsNew.directions.firstBid", "Время отклика"),
      hint: t(
        "analyticsNew.directions.firstBidHint",
        "Время от публикации заказа до первой сделанной ставки.",
      ),
    },
  ];
  const search = normalizeSearchKey(query);
  const isSearching = Boolean(search);
  const showCharts = !isSearching || showChartsDuringSearch;
  const modeGroups = validRows(routesByMode);
  const groups = (mode === "all" ? ["auto", "air"] : [mode])
    .map((chartMode) => modeGroups.find((group) => group.mode === chartMode) || { mode: chartMode })
    .map((group) => ({
      ...group,
      currencies: validRows(group.byCurrency)
        .sort((a, b) => {
          const rank = (currency) => (currencyOrder.includes(currency) ? currencyOrder.indexOf(currency) : 3);
          return rank(a.currency) - rank(b.currency);
        })
        .map((currency) => ({
          ...currency,
          rows: sortRoutes(
            validRows(currency.routes).filter(
              (row) => normalizeSearchKey(routeName(row)).includes(search),
            ),
            sort,
            { kz: "kk", cn: "zh" }[i18n.language] || i18n.language,
          ),
        })),
    }));
  const hasTables = groups.some((group) => group.currencies.length > 0);
  const found = groups.reduce(
    (total, group) => total + group.currencies.reduce((n, c) => n + c.rows.length, 0),
    0,
  );
  const ask = (row) => {
    if (assistantPending || !onAskAssistant) return;
    onAskAssistant(
      `${t("analyticsNew.assistant.routePrefix", "Расскажи про направление ")}${routeName(row)}`,
      {
        tool: "route-analytics",
        route: routeName(row),
      },
    );
  };

  return (
    <div className={styles.routes}>
      {hasTables && (
        <div className={styles.tools}>
          <input
            type="search"
            aria-label={t("analyticsNew.directions.search", "Поиск: город или маршрут")}
            placeholder={t("analyticsNew.directions.search", "Поиск: город или маршрут")}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setExpanded({});
              setShowChartsDuringSearch(false);
            }}
          />
          {isSearching && (
            <button
              type="button"
              className={styles.chartToggle}
              aria-pressed={showChartsDuringSearch}
              onClick={() => setShowChartsDuringSearch((visible) => !visible)}
            >
              {showChartsDuringSearch
                ? t("analyticsNew.directions.hideCharts", "Скрыть диаграммы")
                : t("analyticsNew.directions.showCharts", "Показать диаграммы")}
            </button>
          )}
          <span role="status">
            {t("analyticsNew.directions.loadedRows", "Строк маршрутов")}: {number(found)}
          </span>
        </div>
      )}
      {groups.map((group) => (
        <Card
          key={group.mode}
          title={
            <span className={styles.modeHeading}>
              <span className={common.carrierModeTag} data-mode={group.mode}>
                {modeLabels[group.mode]}
              </span>
              {t("analyticsNew.directions.allRoutes", "Все направления")}
            </span>
          }
          hint={`${t("analyticsNew.directions.orders", "Заказов")}: ${number(group.orders)} · ${t("analyticsNew.directions.directionCount", "Направлений")}: ${number(group.directions)}. ${t("analyticsNew.directions.pieTableHint", "В графике — доля среди показанных маршрутов, в таблицах — внутри своей валюты. Клик — детали в чате.")}`}
        >
          {showCharts && (
            <div className={styles.chart}>
              <RoutePieChart
                routes={topRoutes}
                mode={group.mode}
                showTitle={false}
                onAskAssistant={onAskAssistant}
                assistantPending={assistantPending}
              />
            </div>
          )}
          {group.currencies.map((currency, index) => {
            const key = `${group.mode}-${currency.currency}-${index}`;
            const code =
              typeof currency.currency === "string" && currency.currency.trim() ? currency.currency : "—";
            const shown = expanded[key] ? currency.rows : currency.rows.slice(0, pageSize);
            return (
              <section
                className={styles.currencyGroup}
                key={key}
                aria-label={`${modeLabels[group.mode]} · ${code}`}
              >
                <header className={styles.currencyHeading}>
                  <span className={styles.symbol} data-currency={code} aria-hidden="true">
                    {currencySymbols[code] || code}
                  </span>
                  <h3>{currencyNames[code] || code}</h3>
                  <span>
                    {code} · {t("analyticsNew.directions.directionCount", "Направлений")}:{" "}
                    {number(currency.directions)} · {t("analyticsNew.directions.orders", "Заказов")}:{" "}
                    {number(currency.orders)}
                  </span>
                </header>
                <div className={styles.tableScroll}>
                  <table
                    className={`${common.table} ${styles.table}`}
                    aria-label={`${modeLabels[group.mode]} · ${code}`}
                  >
                    <thead>
                      <tr>
                        {columns.map((column) => (
                          <th
                            key={column.key}
                            scope="col"
                            className={column.key === "route" ? undefined : common.numeric}
                            aria-label={`${column.label}${column.money ? `, ${code}` : ""}`}
                            aria-sort={
                              sort.key === column.key
                                ? sort.direction === 1
                                  ? "ascending"
                                  : "descending"
                                : "none"
                            }
                          >
                            <span className={styles.columnHeading}>
                              <button
                                type="button"
                                onClick={() =>
                                  setSort({
                                    key: column.key,
                                    direction:
                                      sort.key === column.key
                                        ? -sort.direction
                                        : column.key === "route"
                                          ? 1
                                          : -1,
                                  })
                                }
                              >
                                {column.label}
                                {column.money ? `, ${code}` : ""}
                                {sort.key === column.key && (
                                  <span className={styles.arrow} aria-hidden="true">
                                    {sort.direction === 1 ? "▲" : "▼"}
                                  </span>
                                )}
                              </button>
                              {column.hint && (
                                <button
                                  type="button"
                                  className={styles.infoButton}
                                  aria-label={`${column.label}: ${column.hint}`}
                                  data-tooltip-id={`${tooltipId}-${column.key}`}
                                >
                                  <Info size={14} aria-hidden="true" />
                                </button>
                              )}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((row, rowIndex) => (
                        <tr
                          key={`${routeName(row)}-${rowIndex}`}
                          onClick={() => ask(row)}
                          data-disabled={Boolean(assistantPending)}
                        >
                          <td>
                            <button
                              type="button"
                              className={styles.routeButton}
                              disabled={assistantPending || !onAskAssistant}
                              onClick={(event) => {
                                event.stopPropagation();
                                ask(row);
                              }}
                            >
                              {routeName(row, " — ")}
                            </button>
                          </td>
                          <td className={common.numeric}>{number(row.orders)}</td>
                          <td className={`${common.numeric} ${styles.share}`}>
                            {hasNumber(row.sharePercent) ? `${number(row.sharePercent, 1)}%` : "—"}
                          </td>
                          <td className={common.numeric}>{number(row.avgPrice, 2)}</td>
                          <td className={common.numeric}>{number(row.turnover, 2)}</td>
                          <td className={common.numeric}>
                            {typeof row.firstBidText === "string" && row.firstBidText.trim()
                              ? row.firstBidText
                              : hasNumber(row.firstBidMinutes)
                                ? `${number(row.firstBidMinutes)} ${t("analyticsNew.directions.minutes", "мин")}`
                                : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!shown.length && (
                  <p className={styles.notice}>
                    {t("analyticsNew.directions.noMatches", "Маршруты не найдены")}
                  </p>
                )}
                {currency.rows.length > pageSize && (
                  <button
                    type="button"
                    className={styles.more}
                    onClick={() => setExpanded({ ...expanded, [key]: !expanded[key] })}
                  >
                    {expanded[key]
                      ? t("analyticsNew.directions.collapse", "Свернуть")
                      : `${t("analyticsNew.directions.showAll", "Показать все")}: ${number(currency.rows.length)}`}
                  </button>
                )}
                {currency.isTruncated && (
                  <p className={styles.notice}>
                    {t(
                      "analyticsNew.directions.truncated",
                      "API вернул не все маршруты. Поиск и сортировка работают только по загруженным строкам.",
                    )}
                  </p>
                )}
              </section>
            );
          })}
        </Card>
      ))}
      {columns
        .filter((column) => column.hint)
        .map((column) => (
          <AppTooltip
            key={column.key}
            id={`${tooltipId}-${column.key}`}
            positionStrategy="fixed"
            delayShow={150}
          >
            {column.hint}
          </AppTooltip>
        ))}
    </div>
  );
}
